import React, { useEffect, useMemo, useState } from 'react';
import axios from '../config/api';
import { useToast } from '../context/ToastContext';

export interface ReviewModalLineItem {
  menu_item_id: string;
  name?: string;
  quantity: number;
}

interface ReviewModalProps {
  orderId: string;
  cafeName: string;
  orderItems?: ReviewModalLineItem[];
  isOpen: boolean;
  onClose: () => void;
  onReviewSubmitted: () => void;
}

export default function ReviewModal({
  orderId,
  cafeName,
  orderItems,
  isOpen,
  onClose,
  onReviewSubmitted,
}: ReviewModalProps) {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [itemRatings, setItemRatings] = useState<Record<string, number>>({});
  const [submitting, setSubmitting] = useState(false);
  const { showToast } = useToast();

  const aggregatedLines = useMemo(() => {
    if (!orderItems?.length) return [];
    const m = new Map<string, { menu_item_id: string; name: string; quantity: number }>();
    for (const it of orderItems) {
      const prev = m.get(it.menu_item_id);
      const label = it.name?.trim() || 'Item';
      if (prev) prev.quantity += it.quantity;
      else m.set(it.menu_item_id, { menu_item_id: it.menu_item_id, name: label, quantity: it.quantity });
    }
    return Array.from(m.values());
  }, [orderItems]);

  useEffect(() => {
    if (isOpen) {
      setRating(5);
      setComment('');
      setItemRatings({});
    }
  }, [isOpen, orderId]);

  const applyOverallToItems = () => {
    const next: Record<string, number> = {};
    aggregatedLines.forEach((line) => {
      next[line.menu_item_id] = rating;
    });
    setItemRatings(next);
  };

  const setItemStar = (menuItemId: string, value: number) => {
    setItemRatings((prev) => ({ ...prev, [menuItemId]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (rating < 1 || rating > 5) return;

    const item_ratings = Object.entries(itemRatings)
      .filter(([, r]) => r >= 1 && r <= 5)
      .map(([menu_item_id, r]) => ({ menu_item_id, rating: r }));

    setSubmitting(true);
    try {
      await axios.post('/api/reviews', {
        order_id: orderId,
        rating,
        comment: comment.trim() || undefined,
        ...(item_ratings.length > 0 ? { item_ratings } : {}),
      });
      showToast('Thank you for your review!', 'success');
      onReviewSubmitted();
      onClose();
    } catch (error: any) {
      showToast(error.response?.data?.error || 'Failed to submit review', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 my-8 dark:bg-gray-800 dark:text-gray-100">
        <h2 className="text-2xl font-bold mb-4 text-amber-800 dark:text-amber-200">Rate Your Order</h2>
        <p className="text-gray-600 dark:text-gray-300 mb-4">How was your experience at {cafeName}?</p>

        <form onSubmit={handleSubmit}>
          <div className="mb-6">
            <label className="block text-sm font-medium mb-2">Overall rating</label>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  className={`text-3xl transition ${
                    star <= rating ? 'text-yellow-400' : 'text-gray-300 dark:text-gray-600'
                  }`}
                >
                  ★
                </button>
              ))}
            </div>
          </div>

          {aggregatedLines.length > 0 && (
            <div className="mb-6 border-t border-gray-200 dark:border-gray-600 pt-4">
              <div className="flex items-center justify-between gap-2 mb-3">
                <label className="block text-sm font-medium">Rate what you ordered (optional)</label>
                <button
                  type="button"
                  onClick={applyOverallToItems}
                  className="text-xs font-semibold text-amber-700 hover:text-amber-900 dark:text-amber-300"
                >
                  Use overall for all
                </button>
              </div>
              <ul className="space-y-4 max-h-48 overflow-y-auto pr-1">
                {aggregatedLines.map((line) => (
                  <li key={line.menu_item_id} className="text-sm">
                    <p className="text-gray-700 dark:text-gray-200 font-medium mb-1">
                      {line.quantity}× {line.name}
                    </p>
                    <div className="flex gap-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => setItemStar(line.menu_item_id, star)}
                          className={`text-xl transition ${
                            star <= (itemRatings[line.menu_item_id] || 0)
                              ? 'text-yellow-400'
                              : 'text-gray-300 dark:text-gray-600'
                          }`}
                        >
                          ★
                        </button>
                      ))}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mb-6">
            <label className="block text-sm font-medium mb-2">Comment (optional)</label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              rows={4}
              placeholder="Tell us about your experience..."
            />
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300 transition dark:bg-gray-600 dark:text-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || rating < 1}
              className="flex-1 bg-amber-700 text-white px-4 py-2 rounded hover:bg-amber-800 transition disabled:opacity-50"
            >
              {submitting ? 'Submitting...' : 'Submit Review'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
